import React, { useState } from 'react';
import { Badge } from '../../BasicComponents/Badge/Badge';
import { VariantMatrix } from '../VariantMatrix/VariantMatrix';
import { AlternativeItemPopup } from '../AlternativeItemPopup/AlternativeItemPopup';
import { formatMoney } from '../../../utils/currency';
import type { CurrencyCode } from '@bbpos/types';
import type { ProductGridItem, ProductGridProps } from './types';
import type { VariantAttribute, VariantOption } from '../VariantMatrix/types';
import type { AlternativeItem } from '../AlternativeItemPopup/types';

// ─── Skeleton tile ────────────────────────────────────────────────────────────

function SkeletonTile() {
  return (
    <div className="rounded-xl border border-neutral-100 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-3 animate-pulse space-y-2">
      <div className="h-16 rounded-lg bg-neutral-200 dark:bg-neutral-700" />
      <div className="h-3 rounded bg-neutral-200 dark:bg-neutral-700 w-4/5" />
      <div className="h-3 rounded bg-neutral-200 dark:bg-neutral-700 w-1/2" />
    </div>
  );
}

// ─── Product tile ─────────────────────────────────────────────────────────────

function ProductTile({
  product,
  onSelect,
  currency,
  taxInclusive,
}: {
  product: ProductGridItem;
  onSelect: (p: ProductGridItem) => void;
  currency: CurrencyCode;
  taxInclusive?: boolean;
}) {
  const fmt = (paise: number): string => formatMoney(paise, currency);
  // `stock` is already the rolled-up total for a variant parent (the API sums
  // the children), so zero means every variant is out — grey the tile out
  // rather than opening a picker with nothing selectable in it.
  const isOutOfStock = !product.isCombo && product.stock <= 0;
  const showStockHint = !product.isCombo && !product.hasVariants && product.stock > 0;
  // When the POS is set to inclusive pricing, show base + GST (floored per line,
  // matching billing). Combos are already all-inclusive, so leave them as-is.
  const gstRate = product.gstRate ?? 0;
  const showInclusive = taxInclusive && !product.isCombo && gstRate > 0;
  const withTax = (paise: number): number =>
    showInclusive ? paise + Math.floor((paise * gstRate) / 100) : paise;
  // A variant parent sells at its variants' prices: "lowest – highest", or one
  // price when they are all equal. Its own `price` (0 from the wizard) never shows.
  const { priceMin, priceMax } = product;
  const priceText =
    priceMin != null && priceMax != null
      ? priceMin === priceMax
        ? fmt(withTax(priceMin))
        : `${fmt(withTax(priceMin))} – ${fmt(withTax(priceMax))}`
      : fmt(withTax(product.price));

  return (
    <button
      type="button"
      aria-label={
        product.isCombo
          ? `${product.name}, combo, ${fmt(product.price)}`
          : `${product.name}, ${priceText}${showInclusive ? ' incl. tax' : ''}, ${
              isOutOfStock ? 'out of stock' : product.hasVariants ? 'choose variant' : `${product.stock} in stock`
            }`
      }
      onClick={() => onSelect(product)}
      className={[
        'relative flex flex-col gap-2 rounded-xl border p-3 text-left transition-all duration-150 w-full',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
        isOutOfStock
          ? 'border-neutral-100 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 opacity-60 cursor-pointer'
          : 'border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 hover:shadow-md hover:border-brand-200 dark:hover:border-brand-700 active:scale-95',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Image */}
      <div className="h-16 w-full rounded-lg bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center overflow-hidden relative">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" />
        ) : (
          <div
            className="h-full w-full flex items-center justify-center bg-gradient-to-br from-brand-500/80 to-indigo-500/80 dark:from-brand-600/60 dark:to-indigo-600/60 text-white text-2xl font-bold select-none"
            aria-hidden="true"
          >
            {product.name.trim().charAt(0).toUpperCase()}
          </div>
        )}
        {isOutOfStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-neutral-900/70 rounded-lg">
            <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">Out of Stock</span>
          </div>
        )}
      </div>

      {/* Name */}
      <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 line-clamp-2 leading-snug">
        {product.name}
      </p>

      {/* Price row */}
      <div className="mt-auto">
        <div className="flex items-center justify-between gap-1">
          <span className="text-sm font-bold text-neutral-900 dark:text-white">{priceText}</span>
          {product.isCombo && (
            <Badge variant="success" size="sm">Combo</Badge>
          )}
          {product.isLowStock && !isOutOfStock && !product.isCombo && (
            <Badge variant="low" size="sm">Low</Badge>
          )}
          {product.hasVariants && (
            <Badge variant="default" size="sm">Variants</Badge>
          )}
          {showStockHint && !product.isLowStock && (
            <span className="text-[11px] tabular-nums text-neutral-400 dark:text-neutral-500">
              ×{product.stock}
            </span>
          )}
        </div>
        {showInclusive && (
          <p className="text-xs text-neutral-400 dark:text-neutral-500 leading-tight mt-0.5">
            incl. {gstRate}% GST
          </p>
        )}
      </div>
    </button>
  );
}

/**
 * In-stock substitutes for a product, drawn from the same category and
 * ordered by price proximity so the closest match is offered first.
 */
function findAlternatives(
  target: ProductGridItem,
  all: ProductGridItem[],
): AlternativeItem[] {
  return all
    .filter(
      (p) =>
        p.id !== target.id &&
        !p.isCombo &&
        p.stock > 0 &&
        p.categoryId !== undefined &&
        p.categoryId === target.categoryId,
    )
    .sort((a, b) => Math.abs(a.price - target.price) - Math.abs(b.price - target.price))
    .slice(0, 6)
    .map((p) => ({
      id: p.id,
      name: p.name,
      sku: p.sku,
      price: p.price,
      stock: p.stock,
      imageUrl: p.imageUrl,
    }));
}

// ─── ProductGrid ──────────────────────────────────────────────────────────────

export function ProductGrid({
  products,
  onSelect,
  isLoading = false,
  currency = 'INR',
  taxInclusive = false,
  onLoadVariants,
  sellOutOfStock = false,
  className,
}: ProductGridProps) {
  const [variantProduct, setVariantProduct] = useState<ProductGridItem | null>(null);
  const [variantData, setVariantData] = useState<{
    attributes: VariantAttribute[];
    variants: VariantOption[];
  }>({ attributes: [], variants: [] });
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [altProduct, setAltProduct] = useState<ProductGridItem | null>(null);
  // Low stock never blocks the sale: the item is added, and this banner offers
  // substitutes in case the cashier wants to steer the customer elsewhere.
  const [lowStockNudge, setLowStockNudge] = useState<ProductGridItem | null>(null);

  async function handleSelect(product: ProductGridItem) {
    // Zero here covers a variant parent too — its `stock` is the sum of its
    // children, so there is no variant left to pick. Offer substitutes instead
    // of a picker whose every option is disabled. A plain product may still be
    // sold below zero when the caller asks to (it warns first — QA IS1-005).
    if (product.stock <= 0 && !(sellOutOfStock && !product.hasVariants)) {
      setAltProduct(product);
      return;
    }
    // Only open the picker when variants can actually be loaded — otherwise the
    // modal would render an empty body with no way forward. This precedes the
    // low-stock nudge: nothing is in the cart yet for a variant parent, so the
    // nudge's "Added to the cart" would be a lie shown over the picker.
    if (product.hasVariants && onLoadVariants) {
      setVariantProduct(product);
      setVariantData({ attributes: [], variants: [] });
      setVariantsLoading(true);
      try {
        setVariantData(await onLoadVariants(product.id));
      } catch {
        setVariantData({ attributes: [], variants: [] });
      } finally {
        setVariantsLoading(false);
      }
      return;
    }
    // Still add it — a low-stock item is sellable — then surface the option.
    if (product.isLowStock && !product.isCombo) {
      setLowStockNudge(product);
    }
    onSelect(product);
  }

  return (
    <div className={className}>
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <SkeletonTile key={i} />)}
        </div>
      ) : products.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-neutral-400 dark:text-neutral-500 gap-2">
          <svg className="h-10 w-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <p className="text-sm font-medium">No products found</p>
        </div>
      ) : (
        <div
          role="grid"
          aria-label="Products"
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
        >
          {products.map((product) => (
            <div key={product.id} role="gridcell">
              <ProductTile product={product} onSelect={handleSelect} currency={currency} taxInclusive={taxInclusive} />
            </div>
          ))}
        </div>
      )}

      {/* Variant picker */}
      {variantProduct && (
        <VariantMatrix
          isOpen
          onClose={() => setVariantProduct(null)}
          productName={variantProduct.name}
          attributes={variantData.attributes}
          variants={variantData.variants}
          isLoading={variantsLoading}
          currency={currency}
          parentStock={variantProduct.ownStock ?? 0}
          onSelect={(variant) => {
            // Bill the chosen variant, not the parent: it carries its own id,
            // sku, price and stock.
            onSelect({
              ...variantProduct,
              id: variant.id,
              sku: variant.sku,
              price: variant.price,
              stock: variant.stock,
              name: `${variantProduct.name} (${Object.values(variant.attributes).join(', ')})`,
              hasVariants: false,
            });
            setVariantProduct(null);
          }}
        />
      )}

      {/* Alternative item popup */}
      {/* Low-stock nudge — the item is already in the cart; this only offers
          substitutes and can be dismissed. */}
      {lowStockNudge && (
        <div
          role="status"
          className="fixed bottom-4 left-1/2 z-50 w-[min(28rem,92vw)] -translate-x-1/2 rounded-lg border border-warning-300 bg-warning-50 px-4 py-3 shadow-lg dark:border-warning-700 dark:bg-warning-900/40"
        >
          <div className="flex items-start gap-3">
            <div className="flex-1 text-sm text-warning-900 dark:text-warning-100">
              <span className="font-semibold">{lowStockNudge.name}</span> is running low
              {' '}({lowStockNudge.stock} left). Added to the cart.
            </div>
            <div className="flex shrink-0 gap-2">
              {findAlternatives(lowStockNudge, products).length > 0 && (
                <button
                  type="button"
                  className="rounded px-2 py-1 text-sm font-semibold text-warning-800 underline hover:no-underline dark:text-warning-100"
                  onClick={() => {
                    setAltProduct(lowStockNudge);
                    setLowStockNudge(null);
                  }}
                >
                  Alternatives
                </button>
              )}
              <button
                type="button"
                aria-label="Dismiss low stock notice"
                className="rounded px-2 py-1 text-sm text-warning-700 hover:bg-warning-100 dark:text-warning-200 dark:hover:bg-warning-800"
                onClick={() => setLowStockNudge(null)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {altProduct && (
        <AlternativeItemPopup
          isOpen
          onClose={() => setAltProduct(null)}
          originalItemName={altProduct.name}
          alternatives={findAlternatives(altProduct, products)}
          currency={currency}
          onSelect={(alt) => {
            // Route the substitute through handleSelect on its own grid row, so
            // a replacement that is itself a variant parent opens its picker
            // instead of being added with the original's attributes.
            const replacement = products.find((p) => p.id === alt.id);
            setAltProduct(null);
            if (replacement) void handleSelect(replacement);
            else onSelect({ ...altProduct, id: alt.id, name: alt.name, price: alt.price, stock: alt.stock });
          }}
        />
      )}
    </div>
  );
}
